<script setup>
  import { ref, watch, computed, useTemplateRef, nextTick } from 'vue'
  import { cities } from '~/api'
  import { i18n } from '/libs/i18n'

  console.log('vue component loaded')

  defineOptions({
    name: 'CityAutocomplete'
  })

  const opened = ref(false)
  const searchName = ref('')
  const searchCities = ref([])
  const highlightedIndex = ref(-1)
  const autocomplete = useTemplateRef('autocomplete')
  const input = useTemplateRef('autocomplete-input')

  watch(searchName, async (newSearchName, oldSearchName) => {
    if (newSearchName.trim() === '') {
      searchCities.value = []
      return
    }
    await search(newSearchName)
  })

  watch(searchCities, async () => {
    highlightedIndex.value = -1
  })

  const highlightedCities = computed(() => {
    const cities = searchName.value.trim().toLowerCase().split(/\s+/)
                             .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))

    return searchCities.value.map(city => {
      const regex = new RegExp(`(${cities.join('|')})`, 'ig')
      const highlightedName = city.name.replace(regex, '<strong>$1</strong>')
      const highlightedRegionLabel = city.regionLabel.replace(regex, '<strong>$1</strong>')
      return { ...city, highlightedName, highlightedRegionLabel }
    })
  })

  async function open(resetAll = false) {
    if (resetAll) reset()
    opened.value = true
    await nextTick()
    input.value.focus()
  }

  function close() { opened.value = false }

  function reset() {
    searchName.value = ''
    searchCities.value = []
  }

  async function search(name) {
    searchCities.value = await cities.autocomplete({ query: { name } })
  }

  function selectCity(city) {
    close()
    const event = new CustomEvent('citySelected', {
      bubbles: true, detail: { id: city.id, label: city.label }
    })
    autocomplete.value.dispatchEvent(event)
  }

  function highlightPrevious() {
    if (highlightedIndex.value <= 0) return

    highlightedIndex.value -= 1
  }

  function highlightNext() {
    if (highlightedIndex.value >= searchCities.value.length - 1) return

    highlightedIndex.value += 1
  }

  function selectCityFromIndex() {
    if (highlightedIndex.value < 0) return

    const city = searchCities.value[highlightedIndex.value]
    selectCity(city)
  }

  defineExpose({ open, close })
</script>

<template>
  <Transition name="fade">
    <me-autocomplete
      ref="autocomplete"
      v-if="opened"
      @keydown.escape.prevent="close"
      class="fixed inset-0 z-[100000] p-12 pt-12 pb-6"
    >
      <div class="relative z-10 max-w-[1000px] w-full mx-auto bg-rose-50 h-full">
        <div class="max-w-[1000px] w-full mx-auto">
          <input-box
            @keydown.up.prevent="highlightPrevious"
            @keydown.down.prevent="highlightNext"
            @keydown.shift.tab.prevent="highlightPrevious"
            @keydown.tab.exact.prevent="highlightNext"
            @keydown.enter.prevent="selectCityFromIndex"
          >
            <input
              type="text"
              v-model="searchName"
              ref="autocomplete-input"
              :placeholder="i18n.t('js.city_autocomplete.search')"
              class="w-full border border-gray-300 rounded px-3 py-2"
            />

            <ul
              v-if="searchCities.length > 0"
              class="overflow-auto h-[calc(100svh-114px)]"
            >
              <template v-for="(city, index) in highlightedCities" :key="city.id">
                <li
                  :class="[
                    'border-b border-rose-100 bg-rose-50 transition-colors duration-300',
                    index === highlightedIndex ? 'bg-rose-200' : ''
                  ]"
                  @mouseover="highlightedIndex = index"
                >
                  <button
                    type="button"
                    @click="selectCity(city)"
                    class="flex items-center justify-start gap-2 p-4 w-full"
                  >
                    <font-icon class="geolocation--fill icon-size-xl"></font-icon>
                    <span class="text-left">
                      <span v-html="city.highlightedName"></span>
                      <small
                        v-html="city.highlightedRegionLabel"
                        class="mt-[2px] text-gray-500 block"
                      ></small>
                    </span>
                  </button>
                </li>
              </template>
            </ul>

            <no-data
              v-if="searchName.length > 0 && searchCities.length <= 0"
              class="h-[calc(100svh-114px)] flex items-center justify-center text-gray-500"
            >
              {{ i18n.t('js.common.no_result') }}
            </no-data>

            <no-data
              v-else-if="searchCities.length <= 0"
              class="h-[calc(100svh-114px)] flex items-center justify-center text-gray-500"
            >
              <span class="flex flex-col items-center">
                <font-icon class="no-data-icon search mb-2"></font-icon>
                <span class="no-data-label">
                  {{ i18n.t('js.city_autocomplete.search_desc') }}
                </span>
              </span>
            </no-data>
          </input-box>
        </div>
      </div>

      <button
        type="button"
        class="absolute inset-0 z-0 bg-rose-50"
        @click="close"
      >
        <span class="absolute top-0 left-0 right-0 h-12">
          <span class="flex max-w-[1200px] w-full mx-auto justify-end">
            <span class="flex items-center justify-center h-12 w-12">
              <font-icon class="cross icon-size-lg"></font-icon>
            </span>
          </span>
        </span>
      </button>
    </me-autocomplete>
  </Transition>
</template>


<style>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 350ms ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .no-data-message {
    display: grid;
    gap: var(--space-2);
  }
  .no-data-icon {
    font-size: 44px;
    color: var(--color-neutral-light);
  }
  font-icon.no-data-icon::before {
    content: "\1F50D";
    font-size: 1.5rem;
    line-height: 1;
  }
  font-icon.cross::before {
    content: "\2715";
    font-size: 1.5rem;
    line-height: 1;
  }
</style>